import React, { useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import MuestrasMovimientoCamara from "../Muestras/MuestrasMovimientoCamara";
import {
  DESTINO_CLIENTE,
  emptyEnvioDestino,
  toMovimientoDestinoPayload,
} from "../Muestras/EnvioDestinoFields";
import { leaveMeetingSamples } from "../../api/Meetings/meetingSessionsApi";

/**
 * Prefill destino Cliente from meeting contacts / customerName.
 */
export function buildLeaveSamplesDestino(session) {
  const base = {
    ...emptyEnvioDestino(),
    TIPO_DESTINO: DESTINO_CLIENTE,
  };
  const contacts = Array.isArray(session?.clientContacts)
    ? session.clientContacts
    : [];
  const first = contacts.find((c) => Number(c?.customerId) > 0) || contacts[0];
  if (first) {
    const customerId = Number(first.customerId);
    const contactId = Number(first.contactId);
    if (Number.isInteger(customerId) && customerId > 0) {
      base.ID_CLIENTE = customerId;
    }
    if (Number.isInteger(contactId) && contactId > 0) {
      base.ID_CONTACTO = contactId;
    }
    const name = String(first.name || "").trim();
    if (name && !base.ID_CONTACTO) {
      base.PERSONA_ENVIO = name;
    }
  }
  if (!base.PERSONA_ENVIO && !base.ID_CONTACTO) {
    const fallback = String(session?.customerName || "").trim();
    if (fallback) base.PERSONA_ENVIO = fallback;
  }
  return base;
}

export default function MeetingLeaveSamplesDialog({
  open,
  onClose,
  session,
  onDone,
}) {
  const { t } = useTranslation();
  const sessionId = Number(session?.id);

  const initialDestino = useMemo(
    () => buildLeaveSamplesDestino(session),
    // Re-compute when session id / contacts change, not every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      session?.id,
      session?.customerName,
      JSON.stringify(
        (session?.clientContacts || []).map((c) => [
          c.customerId,
          c.contactId,
          c.name,
        ]),
      ),
    ],
  );

  const customRegister = useCallback(
    async ({ items, destino, numCourier, setProgress }) => {
      const destinoPayload = toMovimientoDestinoPayload(destino);
      setProgress?.(0);
      const data = await leaveMeetingSamples(sessionId, {
        samples: items.map((item) => ({
          idMuestra: item.ID_MUESTRA,
          muestraNombre: item.MUESTRA,
        })),
        ID_CLIENTE: destinoPayload.ID_CLIENTE,
        ID_CONTACTO: destinoPayload.ID_CONTACTO,
        PERSONA_ENVIO: destinoPayload.PERSONA_ENVIO,
        NUM_COURIER: numCourier || null,
        FECHA_ENVIO: dayjs().format("YYYY-MM-DD"),
      });
      setProgress?.(items.length);

      const leaveResult = data?.leaveResult || {};
      const failedItems = Array.isArray(leaveResult.failedItems)
        ? leaveResult.failedItems
        : [];
      const failedIds = new Set(
        failedItems.map((f) => Number(f.idMuestra)).filter(Boolean),
      );
      const okCount = Number(leaveResult.ok ?? items.length - failedIds.size);

      return {
        okCount,
        failedIds: [...failedIds],
        successMessage: t("Meetings.leaveSamplesSuccess", { count: okCount }),
        errorMessage: t("Meetings.leaveSamplesError"),
        session: data,
        leaveResult,
      };
    },
    [sessionId, t],
  );

  const handleRegistered = useCallback(
    async (result) => {
      if (result?.session && typeof onDone === "function") {
        await onDone(result.session);
      }
      onClose?.();
    },
    [onClose, onDone],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={typeof window !== "undefined" && window.innerWidth < 600}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          {t("Meetings.leaveSamplesTitle")}
        </Typography>
        <IconButton aria-label={t("Meetings.cancel")} onClick={onClose}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("Meetings.leaveSamplesBody")}
        </Typography>
        {open && Number.isInteger(sessionId) && sessionId > 0 ? (
          <MuestrasMovimientoCamara
            embedded
            title=""
            confirmLabel={t("Meetings.leaveSamplesConfirm")}
            initialDestino={initialDestino}
            allowedTipos={[DESTINO_CLIENTE]}
            hideCourier
            preferGunMode
            customRegister={customRegister}
            onRegistered={handleRegistered}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
